import React from 'react';
import api from '../../../lib/api';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../../components/ui/dialog';
import { Separator } from '../../../components/ui/separator';
import { AlertTriangle, Package, Zap } from 'lucide-react';
import { toast } from 'sonner';

const T = { subtle: '#27272a', border: '#3f3f46' };

export const ConsolidationReportDialog = ({
  open, onOpenChange, loading, data, tab, setTab, onReload
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-3xl bg-zinc-900 border-zinc-700 text-white max-h-[85vh] overflow-hidden flex flex-col">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 text-amber-400" />
          Analiza & Consolidare Stoc
        </DialogTitle>
        <DialogDescription className="text-zinc-400">Recomandari pentru optimizarea locatiilor BIN</DialogDescription>
      </DialogHeader>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <div className="w-8 h-8 border-2 border-amber-400 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : data ? (
        <div className="flex-1 overflow-hidden flex flex-col gap-4">
          {/* Summary Cards */}
          <div className="grid grid-cols-4 gap-2">
            <div className="p-3 rounded-xl text-center" style={{ background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.2)' }}>
              <p className="text-2xl font-extrabold text-emerald-400">{data.summary.health_score}%</p>
              <p className="text-[10px] text-emerald-300 mt-0.5">Scor Sanatate</p>
            </div>
            <div className="p-3 rounded-xl text-center" style={{ background: T.subtle }}>
              <p className="text-2xl font-extrabold text-blue-400">{data.summary.articles_with_stock}</p>
              <p className="text-[10px] text-zinc-400 mt-0.5">Articole cu Stoc</p>
            </div>
            <div className="p-3 rounded-xl text-center" style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)' }}>
              <p className="text-2xl font-extrabold text-red-400">{data.summary.articles_without_preferred_bin}</p>
              <p className="text-[10px] text-red-300 mt-0.5">Fara BIN Preferat</p>
            </div>
            <div className="p-3 rounded-xl text-center" style={{ background: 'rgba(249,115,22,0.08)', border: '1px solid rgba(249,115,22,0.2)' }}>
              <p className="text-2xl font-extrabold text-orange-400">{data.summary.articles_dispersed_3plus}</p>
              <p className="text-[10px] text-orange-300 mt-0.5">Stoc Dispersat (3+)</p>
            </div>
          </div>

          {/* Tab navigation */}
          <div className="flex gap-1 p-1 rounded-lg w-fit" style={{ background: T.subtle }}>
            {[
              { id: 'summary', label: 'Sumar', count: null },
              { id: 'no_preferred', label: 'Fara BIN Preferat', count: data.no_preferred_bin?.length },
              { id: 'dispersed', label: 'Stoc Dispersat', count: data.dispersed_stock?.length },
            ].map(t => (
              <button key={t.id} data-testid={`consolidation-tab-${t.id}`}
                className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${tab === t.id ? 'bg-amber-500 text-white shadow-lg' : 'text-zinc-400 hover:text-white'}`}
                onClick={() => setTab(t.id)}>
                {t.label} {t.count != null && <span className="ml-1 font-mono">({t.count})</span>}
              </button>
            ))}
          </div>

          {/* Tab content */}
          <div className="flex-1 overflow-y-auto space-y-2 pr-1">
            {tab === 'summary' && (
              <div className="space-y-3">
                <div className="p-4 rounded-xl border" style={{ background: T.subtle, borderColor: T.border }}>
                  <h4 className="text-sm font-bold text-zinc-300 mb-2">Rezumat</h4>
                  <div className="space-y-2 text-xs">
                    <div className="flex justify-between"><span className="text-zinc-400">Articole cu BIN preferat setat</span><span className="font-bold text-emerald-400">{data.summary.articles_with_preferred_bin}</span></div>
                    <div className="flex justify-between"><span className="text-zinc-400">Articole fara BIN preferat</span><span className="font-bold text-red-400">{data.summary.articles_without_preferred_bin}</span></div>
                    <div className="flex justify-between"><span className="text-zinc-400">Articole cu stoc pe 3+ locatii</span><span className="font-bold text-orange-400">{data.summary.articles_dispersed_3plus}</span></div>
                    <Separator className="bg-zinc-700" />
                    <div className="flex justify-between"><span className="text-zinc-400">Cantitate totala dispersata</span><span className="font-bold">{data.summary.total_dispersed_qty}</span></div>
                  </div>
                </div>
                {data.summary.health_score < 80 && (
                  <div className="p-3 rounded-xl border border-amber-500/30 bg-amber-500/5">
                    <p className="text-xs text-amber-300 flex items-center gap-2"><AlertTriangle className="w-4 h-4 shrink-0" />Scorul de sanatate este sub 80%. Recomandare: setati BIN-ul preferat pentru toate articolele cu stoc activ si consolidati stocul dispersat.</p>
                  </div>
                )}
                {data.summary.health_score >= 80 && (
                  <div className="p-3 rounded-xl border border-emerald-500/30 bg-emerald-500/5">
                    <p className="text-xs text-emerald-300 flex items-center gap-2"><Package className="w-4 h-4 shrink-0" />Organizarea stocului este buna! Continuati sa setati BIN preferat la articolele noi.</p>
                  </div>
                )}
              </div>
            )}

            {tab === 'no_preferred' && (
              data.no_preferred_bin?.length === 0 ? (
                <div className="text-center py-12"><p className="text-zinc-500 text-sm">Toate articolele cu stoc au BIN preferat setat!</p></div>
              ) : <>
                <div className="p-3 rounded-xl border border-emerald-500/30 bg-emerald-500/5 flex items-center justify-between">
                  <div>
                    <p className="text-sm font-bold text-emerald-300">Auto-Fix Disponibil</p>
                    <p className="text-[10px] text-zinc-400">Seteaza BIN preferat automat pentru articolele cu stoc pe o singura locatie</p>
                  </div>
                  <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white h-8" data-testid="btn-auto-fix"
                    onClick={async () => {
                      try {
                        const res = await api.post('/bins-auto-fix-preferred');
                        toast.success(res.data.message);
                        onReload();
                      } catch (e) { toast.error(e.response?.data?.detail || 'Eroare'); }
                    }}>
                    <Zap className="w-3.5 h-3.5 mr-1" />Auto-Fix
                  </Button>
                </div>
                {data.no_preferred_bin?.map((item, i) => (
                  <div key={i} className="p-3 rounded-xl border border-red-500/20 bg-red-500/5" data-testid={`no-pref-${item.article_code}`}>
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <p className="text-sm font-bold">{item.article_name}</p>
                        <p className="text-[10px] font-mono text-zinc-500">{item.article_code} &middot; {item.total_qty} {item.unit} &middot; {item.locations_count} locatii</p>
                      </div>
                      <Badge className="bg-red-500/20 text-red-300 border-red-500/30 text-[10px]">Fara Preferat</Badge>
                    </div>
                    <div className="flex gap-1.5 flex-wrap mb-2">
                      {item.locations.map((loc, j) => (
                        <span key={j} className="text-[10px] font-mono px-2 py-0.5 rounded bg-zinc-800 text-zinc-400 border border-zinc-700">
                          {loc.cabinet}&rarr;{loc.shelf}&rarr;{loc.position} <span className="text-emerald-400">{loc.quantity}</span>
                        </span>
                      ))}
                    </div>
                    <div className="flex items-center gap-2 p-2 rounded-lg bg-blue-500/10 border border-blue-500/20">
                      <Zap className="w-3.5 h-3.5 text-blue-400 shrink-0" />
                      <p className="text-[11px] text-blue-300">{item.recommendation}</p>
                    </div>
                  </div>
                ))}
              </>
            )}

            {tab === 'dispersed' && (
              data.dispersed_stock?.length === 0 ? (
                <div className="text-center py-12"><p className="text-zinc-500 text-sm">Niciun articol cu stoc dispersat pe 3+ locatii!</p></div>
              ) : data.dispersed_stock?.map((item, i) => (
                <div key={i} className="p-3 rounded-xl border border-orange-500/20 bg-orange-500/5" data-testid={`dispersed-${item.article_code}`}>
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <p className="text-sm font-bold">{item.article_name}</p>
                      <p className="text-[10px] font-mono text-zinc-500">{item.article_code} &middot; {item.total_qty} {item.unit}</p>
                    </div>
                    <Badge className="bg-orange-500/20 text-orange-300 border-orange-500/30 text-[10px]">{item.locations_count} locatii</Badge>
                  </div>
                  <div className="flex gap-1.5 flex-wrap mb-2">
                    {item.locations.map((loc, j) => (
                      <span key={j} className={`text-[10px] font-mono px-2 py-0.5 rounded border ${loc.is_preferred ? 'bg-yellow-500/15 border-yellow-500/40 text-yellow-300' : 'bg-zinc-800 border-zinc-700 text-zinc-400'}`}>
                        {loc.is_preferred && '\u2605 '}{loc.cabinet}&rarr;{loc.shelf}&rarr;{loc.position} <span className="text-emerald-400">{loc.quantity}</span>
                      </span>
                    ))}
                  </div>
                  <div className="flex items-center gap-2 p-2 rounded-lg bg-blue-500/10 border border-blue-500/20">
                    <Zap className="w-3.5 h-3.5 text-blue-400 shrink-0" />
                    <p className="text-[11px] text-blue-300">{item.recommendation}</p>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      ) : null}
    </DialogContent>
  </Dialog>
);
